/**
 * SEO Wrapper Components
 * 
 * Pre-configured SEO wrappers for each page in the application
 */

import React from 'react';
import { useLocation } from 'react-router-dom';
import SEO, { 
  SEOConfigs, 
  getOrganizationSchema, 
  getWebApplicationSchema, 
  getDatasetSchema,
  getBreadcrumbSchema 
} from './SEO';

interface SEOWrapperProps {
  children: React.ReactNode;
}

/**
 * Home Page SEO
 */
export const HomePageSEO: React.FC<SEOWrapperProps> = ({ children }) => {
  const jsonLd = [
    getOrganizationSchema(),
    getWebApplicationSchema(),
    getDatasetSchema()
  ];

  return (
    <>
      <SEO
        {...SEOConfigs.home}
        jsonLd={jsonLd}
      />
      {children}
    </>
  );
};

/**
 * Trade Codes Page SEO
 */
export const TradeCodesSEO: React.FC<SEOWrapperProps> = ({ children }) => {
  const breadcrumbs = getBreadcrumbSchema([
    { name: 'Home', url: 'https://valuechains.ai/' },
    { name: 'Trade Codes', url: 'https://valuechains.ai/trade-codes' }
  ]);

  return (
    <>
      <SEO
        {...SEOConfigs.tradeCodes}
        jsonLd={breadcrumbs}
      />
      {children}
    </>
  );
};

/**
 * Technology Types Page SEO
 */
export const TechnologyTypesSEO: React.FC<SEOWrapperProps> = ({ children }) => {
  const breadcrumbs = getBreadcrumbSchema([
    { name: 'Home', url: 'https://valuechains.ai/' },
    { name: 'Technology Types', url: 'https://valuechains.ai/technology-types' }
  ]);

  return (
    <>
      <SEO
        {...SEOConfigs.technologyTypes}
        jsonLd={breadcrumbs}
      />
      {children}
    </>
  );
};

/**
 * Systems Page SEO
 */
export const SystemsSEO: React.FC<SEOWrapperProps> = ({ children }) => {
  const breadcrumbs = getBreadcrumbSchema([
    { name: 'Home', url: 'https://valuechains.ai/' },
    { name: 'Systems', url: 'https://valuechains.ai/systems' }
  ]);

  return (
    <>
      <SEO
        {...SEOConfigs.systems}
        jsonLd={breadcrumbs}
      />
      {children}
    </>
  );
};

/**
 * Companies Page SEO
 */
export const CompaniesSEO: React.FC<SEOWrapperProps> = ({ children }) => {
  const breadcrumbs = getBreadcrumbSchema([
    { name: 'Home', url: 'https://valuechains.ai/' },
    { name: 'Companies', url: 'https://valuechains.ai/companies' }
  ]);

  return (
    <>
      <SEO
        {...SEOConfigs.companies}
        jsonLd={breadcrumbs}
      />
      {children}
    </>
  );
};

/**
 * About Page SEO
 */
export const AboutSEO: React.FC<SEOWrapperProps> = ({ children }) => {
  const breadcrumbs = getBreadcrumbSchema([
    { name: 'Home', url: 'https://valuechains.ai/' },
    { name: 'About', url: 'https://valuechains.ai/about' }
  ]);

  return (
    <>
      <SEO
        {...SEOConfigs.about}
        jsonLd={breadcrumbs}
      />
      {children}
    </>
  );
};

/**
 * Auth Pages SEO (Login/Register)
 */
export const AuthSEO: React.FC<SEOWrapperProps> = ({ children }) => {
  return (
    <>
      <SEO
        {...SEOConfigs.auth}
      />
      {children}
    </>
  );
};

/**
 * Generic Page SEO with automatic breadcrumbs
 */
export const GenericPageSEO: React.FC<{ 
  title: string; 
  description: string;
  children: React.ReactNode;
}> = ({ title, description, children }) => {
  const location = useLocation();
  const canonical = `https://valuechains.ai${location.pathname}`;
  
  const breadcrumbs = getBreadcrumbSchema([
    { name: 'Home', url: 'https://valuechains.ai/' },
    { name: title, url: canonical }
  ]);

  return (
    <>
      <SEO
        title={title}
        description={description}
        canonical={canonical}
        jsonLd={breadcrumbs}
      />
      {children}
    </>
  );
};

